"use client"

import { useEffect, useState } from "react"

import { FREE_ARTICLE_LIMIT } from "./meter-limit"

// The article meter, CLIENT half: the first N articles each calendar month are
// free, and the N+1th offers the subscription ($9.99/month or $99/year -
// web/lib/billing/plans.ts basic-monthly / basic-yearly; the $49 community
// membership reads with no cap).
//
// The ENFORCEMENT is on the server, in one signed cookie
// (lib/articles/meter-cookie.ts): web/proxy.ts spends every GET for an article
// slug and walls requests for unopened articles after the allowance. Request
// shape and prefetch headers do not bypass that count. This module has two
// client-side jobs:
//
//  1. It reports an article reached by an IN-SITE client navigation to the
//     server (`/api/articles/entitlement?open=`), which returns the current
//     ruling and remaining count. The article GET may already have spent that
//     slug through the proxy; meter-cookie.ts makes the report idempotent in
//     that case, so the reader is not charged twice. The report is also the
//     same-origin backstop for the client wall when a page is already cached.
//  2. It keeps a localStorage mirror of the count so the wall and running
//     "N more free articles" notice render instantly. The mirror is UX, not the
//     server counter, and does not make parallel cookie updates atomic.
//
// The article pages stay fully static: `/articles/[slug]` is generated with
// `dynamicParams = false` across every article slug, above a comment recording
// that `force-dynamic` once cost 1.7s to first byte AND broke real 404s.
// Nothing in the ROUTE is per-visitor.
//
// The same static article page and cookie rule apply to crawlers and people.
// A request with no meter cookie starts a fresh allowance; that is not a
// user-agent exception. Never branch on user agent to decide any of this:
// branching on user agent IS cloaking.

/** Articles a reader gets free each calendar month before the subscription
 * wall. Defined once in ./meter-limit.ts, which ./meter-cookie.ts (the proxy's
 * and the entitlement route's one meter rule) reads too, so the wall and the
 * server can never disagree. */
export { FREE_ARTICLE_LIMIT }

/** Start warning the reader when this many free articles remain. */
export const METER_WARN_REMAINING = 2

// Distinct slugs OPENED this month. Deliberately separate from
// `ca:articles-read:v1` in ./read.ts, which records articles FINISHED. The v2
// shape is `{ m: "YYYY-MM", s: [slug, ...] }` so the allowance resets each
// calendar month, matching the server meter; the old v1 key held a bare array
// with no month and is simply ignored.
const OPENED_KEY = "ca:articles-opened:v2"
const CHANGE_EVENT = "ca:articles-opened-changed"

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7)
}

function readOpened(): Set<string> {
  if (typeof window === "undefined") return new Set()
  try {
    const raw = window.localStorage.getItem(OPENED_KEY)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    if (parsed?.m !== currentMonth() || !Array.isArray(parsed.s)) return new Set()
    return new Set(parsed.s.filter((v: unknown): v is string => typeof v === "string"))
  } catch {
    return new Set()
  }
}

function writeOpened(set: Set<string>) {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(
      OPENED_KEY,
      JSON.stringify({ m: currentMonth(), s: Array.from(set) }),
    )
    queueMicrotask(() => window.dispatchEvent(new Event(CHANGE_EVENT)))
  } catch {
    // Private mode or a full quota. Failing open is correct here: the server
    // meter is the enforcement, this mirror is only UX.
  }
}

/** The server's ruling on an article reached by an in-site click. */
type ServerRuling = { gated: boolean; remaining: number | null }

// One report per article at a time: the wall and the notice both ask on the
// same render. Only in-flight reports are shared; a settled ruling is never
// reused, so a reader who subscribes in another tab is asked afresh.
const reportsInFlight = new Map<string, Promise<ServerRuling | null>>()

/**
 * Is the current navigation document itself this article? This only skips a
 * redundant client report after a direct document GET, which the proxy already
 * meters. Client navigations and prefetched-page activations still report so
 * the gate receives the server's current ruling; if their GET already spent
 * the slug, the shared cookie rule makes that report idempotent. Asked only
 * once the page is shown: during a prerender `activationStart` still reads 0.
 */
function loadedAsThisDocument(slug: string): boolean {
  try {
    const nav = performance.getEntriesByType("navigation")[0] as
      | (PerformanceNavigationTiming & { activationStart?: number; deliveryType?: string })
      | undefined
    if (!nav || (nav.activationStart ?? 0) > 0) return false
    if (nav.deliveryType === "navigational-prefetch") return false
    return new URL(nav.name).pathname === `/articles/${slug}`
  } catch {
    return false
  }
}

/** Report a client-side article open and return the server's ruling. The proxy
 * may already have spent the slug on its GET; the shared cookie rule does not
 * charge it twice. Return null when the server could not be asked, leaving the
 * local mirror as the client-UI fallback. */
function reportInSiteOpen(slug: string): Promise<ServerRuling | null> {
  const inFlight = reportsInFlight.get(slug)
  if (inFlight) return inFlight
  const report = fetch(`/api/articles/entitlement?open=${encodeURIComponent(slug)}`, {
    credentials: "same-origin",
    cache: "no-store",
    headers: { accept: "application/json" },
  })
    .then((res) => (res.ok ? (res.json() as Promise<{ gated?: unknown; remaining?: unknown }>) : null))
    .then((body) =>
      body
        ? {
            gated: body.gated === true,
            remaining: typeof body.remaining === "number" ? body.remaining : null,
          }
        : null,
    )
    .catch(() => null)
  reportsInFlight.set(slug, report)
  void report.finally(() => reportsInFlight.delete(slug))
  return report
}

export type MeterState = {
  /** The wall should be shown. */
  gated: boolean
  /** Distinct articles opened so far this month. */
  opened: number
  /** Free articles left before the wall, floored at 0. */
  remaining: number
}

/**
 * Record that this slug was opened and report the resulting meter state.
 *
 * Re-opening an article already counted this month stays free. Someone who
 * bookmarked article three and comes back to it should never be walled out of
 * a page he has already been given - the server meter keeps the same rule.
 */
export function recordArticleOpen(slug: string): MeterState {
  const set = readOpened()

  if (set.has(slug)) {
    return {
      gated: false,
      opened: set.size,
      remaining: Math.max(0, FREE_ARTICLE_LIMIT - set.size),
    }
  }

  if (set.size >= FREE_ARTICLE_LIMIT) {
    // Deliberately NOT added to the set. An article he never got to read must
    // not consume his allowance, so the count stays honest if he subscribes.
    return { gated: true, opened: set.size, remaining: 0 }
  }

  set.add(slug)
  writeOpened(set)
  return {
    gated: false,
    opened: set.size,
    remaining: Math.max(0, FREE_ARTICLE_LIMIT - set.size),
  }
}

const OPEN_STATE: MeterState = {
  gated: false,
  opened: 0,
  remaining: FREE_ARTICLE_LIMIT,
}

/**
 * Meter state for the article currently being read.
 *
 * `ready` is false on the server and on the first client render, and the state
 * it reports until then is ungated. That ordering matters twice over: it keeps
 * hydration identical between server and client (the repo has already been
 * bitten by a render attribute derived from localStorage-seeded state), and it
 * means the article body is always present in the delivered HTML.
 *
 * `walledByServer` is the server's own ruling that this reader has spent his
 * allowance and does not read with no cap; the wall then sends him to the
 * subscribe page, as the proxy does for every other request past the limit.
 */
export function useArticleMeter(slug: string): MeterState & {
  ready: boolean
  walledByServer: boolean
} {
  const [state, setState] = useState<MeterState>(OPEN_STATE)
  const [ready, setReady] = useState(false)
  // Keyed by slug, so a ruling for the article read before an in-site click
  // never stands in for the next one.
  const [server, setServer] = useState<{ slug: string; ruling: ServerRuling } | null>(null)

  useEffect(() => {
    let active = true
    // Scheduled on a frame rather than called synchronously. A setState during
    // the effect body triggers a cascading render, which the lint rule catches
    // and which costs a wasted paint on every article open. The meter reads
    // localStorage, so a frame's delay is invisible; `ready` gates the wall so
    // nothing flashes before the count is known.
    const raf = requestAnimationFrame(() => {
      setState(recordArticleOpen(slug))
      setReady(true)
    })

    // A direct document GET was already spent by the proxy. Other navigations
    // report to get the current ruling; the server report is idempotent if the
    // route request or prefetch already spent the slug. A prerender waits until
    // activation before making this client report.
    function reportUnlessPageLoad() {
      if (!active || loadedAsThisDocument(slug)) return
      void reportInSiteOpen(slug).then((ruling) => {
        if (active && ruling) setServer({ slug, ruling })
      })
    }
    const prerendering = (document as Document & { prerendering?: boolean }).prerendering === true
    if (prerendering) {
      document.addEventListener("prerenderingchange", reportUnlessPageLoad, { once: true })
    } else {
      reportUnlessPageLoad()
    }

    // Another tab may have opened articles in the meantime.
    function refresh() {
      setState(recordArticleOpen(slug))
    }
    function onStorage(e: StorageEvent) {
      if (e.key === OPENED_KEY) refresh()
    }
    window.addEventListener("storage", onStorage)
    window.addEventListener(CHANGE_EVENT, refresh)
    return () => {
      active = false
      cancelAnimationFrame(raf)
      document.removeEventListener("prerenderingchange", reportUnlessPageLoad)
      window.removeEventListener("storage", onStorage)
      window.removeEventListener(CHANGE_EVENT, refresh)
    }
  }, [slug])

  const ruling = server?.slug === slug ? server.ruling : null
  const walledByServer = ruling?.gated === true
  return {
    ...state,
    gated: state.gated || walledByServer,
    // The server's count when it has given one: it is the one that decides.
    remaining: ruling?.remaining ?? state.remaining,
    ready,
    walledByServer,
  }
}
